// client/src/dnd/views/Interactive_parts/GeomanControl.js
import { useRef, useEffect } from 'react';
import L from 'leaflet';

const apiBase = '/api/polygons';

function GeomanControl({
  mapId,
  userId,
  onShapesChanged = () => {},
  onZonesChanged = () => {},
  onShapeClick,
  shapeLayerMap,
  onButtonClick,
  visibleZoneIds,
  canEdit,
  // Optional hooks
  onPathingEntriesChange,
  onGeomanActiveChange,
}) {
  const { useMap } = require('react-leaflet');
  const map = useMap();
  const shapesLayer = useRef(null);
  const allLayersById = useRef({});

  // Helper: rebuild entries for pathing etc.
  const rebuildEntries = () => {
    if (typeof onPathingEntriesChange !== 'function') return;
    const entries = Object.entries(allLayersById.current).map(([id, layer]) => ({
      id: Number(id),
      shape: layer._shapeMeta || {},
      layer,
    }));
    onPathingEntriesChange(entries);
  };

  // Global Geoman options: disable snapping like before
  useEffect(() => {
    if (!map || !canEdit || !map.pm) return;
    try {
      map.pm.setGlobalOptions({
        snap: false,
        snappable: false,
        snapDistance: 0,
        snapMiddle: false,
        snapSegment: false,
        allowSelfIntersection: true,
      });

      if (map.pm.Draw) {
        Object.keys(map.pm.Draw).forEach(drawKey => {
          if (
            typeof map.pm.Draw[drawKey] === 'object' &&
            map.pm.Draw[drawKey]?.setOptions
          ) {
            map.pm.Draw[drawKey].setOptions({
              snap: false,
              snappable: false,
              snapDistance: 0,
            });
          }
        });
      }

      map.eachLayer(layer => {
        if (layer.pm && typeof layer.pm.setOptions === 'function') {
          layer.pm.setOptions({
            snap: false,
            snappable: false,
            snapDistance: 0,
          });
        }
      });
    } catch (e) {}
  }, [map, canEdit]);

  // Track Geoman active state (draw/edit/cut) for parent if requested
  useEffect(() => {
    if (!map || !canEdit || typeof onGeomanActiveChange !== 'function') return;

    const activate = () => onGeomanActiveChange(true);
    const deactivate = () => onGeomanActiveChange(false);

    map.on('pm:drawstart', activate);
    map.on('pm:drawend', deactivate);
    map.on('pm:editstart', activate);
    map.on('pm:editend', deactivate);
    map.on('pm:cutstart', activate);
    map.on('pm:cutend', deactivate);

    return () => {
      map.off('pm:drawstart', activate);
      map.off('pm:drawend', deactivate);
      map.off('pm:editstart', activate);
      map.off('pm:editend', deactivate);
      map.off('pm:cutstart', activate);
      map.off('pm:cutend', deactivate);
      onGeomanActiveChange(false);
    };
  }, [map, canEdit, onGeomanActiveChange]);

  // Add/remove Geoman controls
  useEffect(() => {
    if (!map || !map.pm) return;

    map.pm.removeControls();
    if (canEdit) {
      map.pm.addControls({
        position: 'topleft',
        drawCircle: true,
        drawCircleMarker: false,
        drawMarker: false,
        drawPolyline: true,
        cutPolygon: false,
        dragMode: false,
        editMode: false,
        removalMode: false,
        drawRectangle: true,
        drawPolygon: true,
        rotateMode: false,
      });
    }

    return () => {
      if (map.pm) {
        map.pm.removeControls();
      }
    };
  }, [map, canEdit]);

  // Initial load of shapes from /api/polygons
  useEffect(() => {
    if (!mapId) return;
    fetch(`${apiBase}?mapId=${mapId}`)
      .then(res => res.json())
      .then(shapes => {
        if (shapesLayer.current) {
          shapesLayer.current.clearLayers();
        } else {
          shapesLayer.current = L.featureGroup().addTo(map);
        }

        if (shapeLayerMap && shapeLayerMap.current) shapeLayerMap.current = {};
        allLayersById.current = {};

        shapes.forEach(shapeRaw => {
          const shape = {
            ...shapeRaw,
            strokeColor: shapeRaw.strokeColor || shapeRaw.stroke_color || '#3388ff',
            fillColor: shapeRaw.fillColor || shapeRaw.fill_color || '#3388ff',
            strokeOpacity:
              shapeRaw.stroke_opacity !== undefined && shapeRaw.stroke_opacity !== null
                ? parseFloat(shapeRaw.stroke_opacity)
                : 1.0,
            fillOpacity:
              shapeRaw.fill_opacity !== undefined && shapeRaw.fill_opacity !== null
                ? parseFloat(shapeRaw.fill_opacity)
                : 0.3,
          };

          const behaviorType =
            shape.behaviorType ??
            shape.behavior_type ??
            null;

          let geometry;
          try {
            geometry =
              typeof shape.geometry === 'string'
                ? JSON.parse(shape.geometry)
                : shape.geometry;
          } catch {
            return;
          }

          let layer;
          if (
            geometry &&
            geometry.type === 'Point' &&
            typeof shape.radius === 'number' &&
            geometry.coordinates
          ) {
            const coords = geometry.coordinates;
            layer = L.circle([coords[1], coords[0]], {
              radius: shape.radius,
              color: shape.strokeColor,
              fillColor: shape.fillColor,
              opacity: shape.strokeOpacity,
              fillOpacity: shape.fillOpacity,
              weight: 3,
            });
          } else {
            const geojsonFeature = {
              type: 'Feature',
              geometry,
              properties: {
                behaviorType,
                behavior_type: behaviorType,
              },
            };
            const layers = L.geoJSON(geojsonFeature, {
              pointToLayer: (_feature, latlng) =>
                L.circleMarker(latlng, {
                  radius: 10,
                  fillColor: shape.fillColor,
                  color: shape.strokeColor,
                  opacity: shape.strokeOpacity,
                  fillOpacity: shape.fillOpacity,
                  weight: 3,
                }),
              style: {
                color: shape.strokeColor,
                fillColor: shape.fillColor,
                opacity: shape.strokeOpacity,
                fillOpacity: shape.fillOpacity,
                weight: 3,
              },
            }).getLayers();
            layer = layers[0];
          }

          if (!layer) return;

          // Core options/meta
          layer._dbId = shape.id;
          layer.options.color = shape.strokeColor;
          layer.options.fillColor = shape.fillColor;
          layer.options.opacity = shape.strokeOpacity;
          layer.options.fillOpacity = shape.fillOpacity;
          layer.options.label = shape.label; // keep data, but we don't show popup
          layer.options.behaviorType = behaviorType;
          layer.options.behavior_type = behaviorType;

          layer._shapeMeta = {
            ...shape,
            behaviorType,
            behavior_type: behaviorType,
          };

          // Ensure no leaflet popup label is shown
          if (typeof layer.unbindPopup === 'function') {
            layer.unbindPopup();
          }

          if (canEdit && onShapeClick) {
            layer.on('click', () => {
              onShapeClick(
                {
                  ...shape,
                  id: shape.id,
                  strokeColor: layer.options.color,
                  fillColor: layer.options.fillColor,
                  strokeOpacity: layer.options.opacity,
                  fillOpacity: layer.options.fillOpacity,
                  label: shape.label,
                  geometry: shape.geometry,
                  radius: shape.radius,
                  shapeType: shape.shapeType,
                  pattern: shape.pattern,
                  behaviorType,
                },
                layer
              );
            });
          } else {
            layer.off('click');
          }

          if (shapeLayerMap && shapeLayerMap.current) {
            shapeLayerMap.current[shape.id] = layer;
          }
          allLayersById.current[shape.id] = layer;
          shapesLayer.current.addLayer(layer);
        });

        if (shapesLayer.current && !map.hasLayer(shapesLayer.current)) {
          shapesLayer.current.addTo(map);
        }

        onShapesChanged();
        onZonesChanged && onZonesChanged();
        rebuildEntries();
      })
      .catch(() => {});
  }, [mapId, map, canEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map) return;

    if (shapesLayer.current) {
      map.removeLayer(shapesLayer.current);
      shapesLayer.current = L.featureGroup().addTo(map);
    } else {
      shapesLayer.current = L.featureGroup().addTo(map);
    }

    function handleDrawStart(_e) {}

    function handleButtonClick(e) {
      if (onButtonClick) onButtonClick(e.btnName);
    }

    if (canEdit) {
      map.on('pm:drawstart', handleDrawStart);
      map.on('pm:buttonclick', handleButtonClick);

      map.on('pm:create', async (e) => {
        const layer = e.layer;
        const isCircle = typeof layer.getRadius === 'function';

        let geometry;
        let radius = null;
        let shapeType = '';

        if (isCircle) {
          const center = layer.getLatLng();
          geometry = {
            type: 'Point',
            coordinates: [center.lng, center.lat],
          };
          radius = layer.getRadius();
          shapeType = 'Circle';
        } else {
          geometry = layer.toGeoJSON().geometry;
          shapeType = geometry.type || '';
        }

        const payload = {
          mapId,
          userId,
          geometry,
          radius,
          shapeType,
          fillColor: layer.options.fillColor || '#3388ff',
          strokeColor: layer.options.color || '#3388ff',
          strokeOpacity: layer.options.opacity ?? 1.0,
          fillOpacity: layer.options.fillOpacity ?? 0.3,
          weight: 3,
          // No default visible label text
          label: '',
          pattern: '',
        };

        try {
          const res = await fetch(apiBase, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          layer._dbId = data.id;

          const behaviorType =
            data.behaviorType ??
            data.behavior_type ??
            null;

          layer.options.behaviorType = behaviorType;
          layer.options.behavior_type = behaviorType;
          layer.options.label = data.label || '';

          layer._shapeMeta = {
            ...data,
            behaviorType,
            behavior_type: behaviorType,
          };

          // Kill any popup that might show the label
          if (typeof layer.unbindPopup === 'function') {
            layer.unbindPopup();
          }

          if (shapeLayerMap && shapeLayerMap.current) {
            shapeLayerMap.current[data.id] = layer;
          }
          allLayersById.current[data.id] = layer;
          shapesLayer.current.addLayer(layer);

          if (onShapeClick) {
            layer.on('click', () => {
              onShapeClick(
                {
                  ...data,
                  strokeColor: data.strokeColor || data.stroke_color || '#3388ff',
                  fillColor: data.fillColor || data.fill_color || '#3388ff',
                  strokeOpacity:
                    data.stroke_opacity !== undefined && data.stroke_opacity !== null
                      ? parseFloat(data.stroke_opacity)
                      : 1.0,
                  fillOpacity:
                    data.fill_opacity !== undefined && data.fill_opacity !== null
                      ? parseFloat(data.fill_opacity)
                      : 0.3,
                  geometry: payload.geometry,
                  radius: payload.radius,
                  shapeType: payload.shapeType,
                  label: data.label || '',
                  pattern: payload.pattern,
                  behaviorType,
                },
                layer
              );
            });
          }

          if (layer.pm && typeof layer.pm.enable === 'function') {
            layer.pm.enable({ allowSelfIntersection: false });
          }
          if (layer.pm && typeof layer.pm.setOptions === 'function') {
            layer.pm.setOptions({
              snap: false,
              snappable: false,
              snapDistance: 0,
            });
          }

          onZonesChanged && onZonesChanged();
          rebuildEntries();
        } catch {}
      });

      map.on('pm:edit', async (e) => {
        e.layers.eachLayer(async layer => {
          if (!layer._dbId) return;

          const isCircle = typeof layer.getRadius === 'function';
          let geometry;
          let radius = null;
          let shapeType = '';

          if (isCircle) {
            const center = layer.getLatLng();
            geometry = {
              type: 'Point',
              coordinates: [center.lng, center.lat],
            };
            radius = layer.getRadius();
            shapeType = 'Circle';
          } else {
            geometry = layer.toGeoJSON().geometry;
            shapeType = geometry.type || '';
          }

          const payload = {
            geometry,
            radius,
            shapeType,
            fillColor: layer.options.fillColor,
            strokeColor: layer.options.color,
            strokeOpacity: layer.options.opacity,
            fillOpacity: layer.options.fillOpacity,
            // keep label in DB but don't use it for visible popup
            label: layer.options.label || '',
            pattern: '',
          };

          try {
            await fetch(`${apiBase}/${layer._dbId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });

            if (layer._shapeMeta) {
              layer._shapeMeta = {
                ...layer._shapeMeta,
                ...payload,
              };
            }

            if (layer.pm && typeof layer.pm.setOptions === 'function') {
              layer.pm.setOptions({
                snap: false,
                snappable: false,
                snapDistance: 0,
              });
            }

            // Ensure popup is removed after edit as well
            if (typeof layer.unbindPopup === 'function') {
              layer.unbindPopup();
            }

            onZonesChanged && onZonesChanged();
            rebuildEntries();
          } catch {}
        });
      });

      map.on('pm:remove', async (e) => {
        if (!e.layer._dbId) return;
        try {
          await fetch(`${apiBase}/${e.layer._dbId}`, { method: 'DELETE' });

          if (shapeLayerMap && shapeLayerMap.current) {
            delete shapeLayerMap.current[e.layer._dbId];
          }
          delete allLayersById.current[e.layer._dbId];
          if (shapesLayer.current) {
            shapesLayer.current.removeLayer(e.layer);
          }

          onZonesChanged && onZonesChanged();
          rebuildEntries();
        } catch {}
      });
    }

    return () => {
      map.off('pm:create');
      map.off('pm:edit');
      map.off('pm:remove');
      map.off('pm:drawstart', handleDrawStart);
      map.off('pm:buttonclick', handleButtonClick);
      if (shapesLayer.current) {
        map.removeLayer(shapesLayer.current);
      }
    };
  }, [
    map,
    mapId,
    userId,
    onZonesChanged,
    canEdit,
    onButtonClick,
    onShapeClick,
    shapeLayerMap,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Respect visibleZoneIds (DM-only overlay)
  useEffect(() => {
    if (!map) return;
    Object.entries(allLayersById.current).forEach(([id, layer]) => {
      const isVisible = visibleZoneIds ? visibleZoneIds.has(Number(id)) : true;
      if (isVisible) {
        if (!map.hasLayer(layer)) map.addLayer(layer);
      } else {
        if (map.hasLayer(layer)) map.removeLayer(layer);
      }
    });
  }, [visibleZoneIds, map]);

  return null;
}

export default GeomanControl;
